import * as SubframeCore from '@subframe/core';
import { Icon } from '@subframe/core';
import {
  ListSubscriptionsResponse,
  UpgradeContentBlock,
  UpgradeContentBlockTableDataCell,
  UpgradePlan,
  UpgradeStage,
  UpgradeStageStep,
  UpgradeStageStepActionType,
  UpgradeStageStepBadgeVariant,
  UserIdentity,
} from 'api/models';
import Page from 'components/Page';
import { Stack } from 'components/utils/Stack';
import { Account } from 'models/account';
import { useEffect, useState } from 'react';
import { NavigateFunction } from 'react-router';
import * as Sentry from '@sentry/browser';
import classNames from 'classnames';
import {
  Actions,
  AtomicTooltip,
  Divider,
  ClickToCopy,
} from 'components/design-system';
import { CommentTooltip } from 'components/design-system/CommentTooltip';
import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import { formatDistanceToNow } from 'date-fns';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import { useDrawer } from 'layouts/dashboard/drawer/context';
import { useSnackbar } from 'notistack';
import { isExampleData } from 'src/data/upgrades';
import { IconWithBackground } from 'subframe/components/IconWithBackground';
import { InformationalHeader } from 'subframe/components/InformationalHeader';
import { Steps } from 'subframe/components/Steps';
import { Table } from 'subframe/components/Table';
import copy from 'copy-to-clipboard';
import {
  AccordionWithUnderline,
  Alert,
  Avatar,
  Badge,
  Breadcrumbs,
  Button,
  CardBase,
  CheckBase,
  Checkbox,
  CtaCard,
  IconButton,
  LearnMoreAccordion,
  RadioGroup,
  StepBase,
  TextArea,
  TextField,
} from 'subframe/index';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { convertToLowerCaseWithDashes } from 'utils/filters';
import styles from './UpgradesView.module.scss';
import {
  UpgradesCreateEditStepDialog,
  UpgradesDeleteStepDialog,
} from './components/DotMenu';
import { UpgradePlanStepMarkdown } from './components/StepMarkdown';
import { UpgradeStepSidebar } from './components/StepSidebar';
import { UpgradeSidebar } from './components/UpgradeSidebar';
import UpgradesSkipStepDialog from './components/UpgradesSkipStepDialog';
import UpgradesUndoSkipStepDialog from './components/UpgradesUndoSkipStepDialog';
import {
  getTooltipForUpgradeRequestedBy,
  getUpgradeModeHumanReadable,
  isPlanActionDisabled,
  UpgradeAny,
  UpgradeViewMode,
} from './helpers/UpgradesHelpers';
import { getSkipReasonHuman } from './helpers/skip_reasons';
import MarkUpgradePlanAsCompletedDialog from './components/MarkUpgradePlanAsCompletedDialog';
import { Diff } from '../../subframe/components/Diff';
import { EVA_AVATAR_IMAGE } from 'constants/images';
import SHARCFlowCheckResults from './components/SHARCFlowCheckResults';
import {
  getUpgradeAssessmentStageStep,
  getUpgradePlanStageStep,
  getUpgradeTemplateStageStep,
  updateUpgradePlanStageStep,
  useListSubscriptions,
} from 'api/frontend';
import UpgradeTemplatesUndoSkipStepDialog from './components/UpgradeTemplatesUndoSkipStepDialog';
import UpgradeTemplateSkipStepDialog from './components/UpgradeTemplateSkipStepDialog';
import { UpgradeFeedbackDialog } from './components/UpgradeFeedbackDialog';
import { UpgradeStepAgentSidebar } from './components/StepAgentSidebar';
import useUserAccountState from 'hooks/useUserAccountState';

function getStageVariant(
  stage: UpgradeStage,
  tabIdx: number,
  stageIdx: number,
):
  | 'default'
  | 'success-inactive'
  | 'error-inactive'
  | 'in-progress-inactive'
  | 'blocked-inactive'
  | 'in-progress-active'
  | 'success-active'
  | 'error-active'
  | 'blocked-active' {
  if (tabIdx === stageIdx) {
    if (stage.status === 'completed') {
      return 'success-active';
    }
    if (stage.status === 'blocked') {
      return 'blocked-active';
    }
    return 'in-progress-active';
  } else {
    if (stage.status === 'completed') {
      return 'success-inactive';
    }
    if (stage.status === 'blocked') {
      return 'blocked-inactive';
    }

    return 'in-progress-inactive';
  }
}

function getBadgeVariant(
  variant: UpgradeStageStepBadgeVariant,
): 'brand' | 'neutral' | 'error' | 'warning' | 'success' {
  if (variant === 'success') {
    return 'success';
  } else if (variant === 'warning') {
    return 'warning';
  } else if (variant === 'error') {
    return 'error';
  } else if (variant === 'neutral') {
    return 'neutral';
  } else {
    return 'neutral';
  }
}

function getStageDisabled(stage: UpgradeStage): boolean {
  return stage.status === 'blocked';
}

function getPlanStagesCompleted(stages: UpgradeStage[]) {
  for (let i = 0; i < stages?.length; i++) {
    if (stages[i].status !== 'completed' && stages[i].status !== 'skipped') {
      return false;
    }
  }
  return true;
}

export function UpgradesViewComponent({
  account,
  upgradePlan,
  mode,
  stages: inputStages,
  navigate,
  basePath,
  breadcrumb,
  onChange,
  additionalHeaderElements,
  actions,
  stagesStepsDefaultOpen,
}: {
  account: Account;
  upgradePlan: UpgradeAny;
  mode: UpgradeViewMode;
  stages: UpgradeStage[];
  navigate: NavigateFunction;
  basePath: string;
  breadcrumb: string;
  onChange: (mutateStage?: boolean, mutatePlan?: boolean) => void;
  additionalHeaderElements: JSX.Element;
  actions?: React.ReactNode;
  stagesStepsDefaultOpen: boolean;
}) {
  const setRightDrawerContents = useDrawer();
  const stages = inputStages.sort((a, b) => a.stage_number - b.stage_number);

  const [tabIdx, setTabIdx] = useState<number>(0);
  const [stepOpen, setStepOpen] = useState<{ [name: string]: boolean }>({});
  const currentStage = stages[tabIdx];
  const showExampleData: boolean =
    upgradePlan?.id !== undefined && isExampleData(upgradePlan.id);
  const { enqueueSnackbar } = useSnackbar();
  const { logEvent } = AnalyticsEventLogger();

  const { currentOrganization } = useUserAccountState();
  const { data: subscriptions } = useListSubscriptions(
    currentOrganization?.slug,
    {},
    {
      request: { headers: { Authorization: `Bearer ${account?.token}` } },
    },
  );

  const [selectedStep, setSelectedStep] = useState<UpgradeStageStep | null>(
    null,
  );
  const [dialogOpen, setDialogOpen] = useState<
    | 'delete'
    | 'create'
    | 'edit'
    | 'skip'
    | 'undo-skip'
    | 'provide-feedback-up'
    | 'provide-feedback-down'
    | undefined
  >(undefined);
  const [addStepPosition, setAddStepPosition] = useState<'before' | 'after'>(
    'after',
  );
  const [selectedTableCell, setSelectedTableCell] = useState<{
    row: number | undefined;
    column: number | undefined;
  }>({ row: undefined, column: undefined });

  const accountBasePath = useAccountIdRoute('/orgs/:orgId/accounts/:accountId');
  // For Addon Upgrade Plan to render tie in when an addon plan branches off from a cluster plan
  const upgradeAddonBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/plans/addons',
  );
  const upgradeTemplateRequestPath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/templates/clusters/new',
  );

  useEffect(() => {
    if (upgradePlan?.id && currentStage) {
      logEvent('upgrade-plan-viewed', {
        upgrade_plan_id: upgradePlan.id,
        currentStageName: currentStage.name,
      });
    }
  }, [upgradePlan?.id]);

  // once all stages are complete, dialog should popup (without page refresh)
  // using hook instead of adding this to Mark as Done button, since last completed steo can be an automatic check
  const [openMarkAsCompleted, setOpenMarkAsCompleted] = useState(
    getPlanStagesCompleted(stages) &&
      upgradePlan.status !== 'completed' &&
      upgradePlan.status !== 'cancelled' &&
      mode === UpgradeViewMode.Plan,
  );
  useEffect(() => {
    if (getPlanStagesCompleted(stages) && upgradePlan.status !== 'completed') {
      setOpenMarkAsCompleted(true);
    }
  }, [stages]);

  const tooltipForAction = (
    step: UpgradeStageStep,
    actionType?: UpgradeStageStepActionType,
  ) => {
    if (showExampleData) {
      return 'This is a demonstration view only at this point - action buttons are disabled in preview mode';
    }
    if (isPlanActionDisabled(upgradePlan.status)) {
      return `${getUpgradeModeHumanReadable(
        mode,
      )} is read-only, action is not available`;
    }
    if (actionType && !step.allowed_action_types?.includes(actionType)) {
      return 'Action is not available for this step';
    }
    return undefined;
  };

  const handleStepActivityDrawerOpen = (step: UpgradeStageStep) => {
    setRightDrawerContents(
      <Breadcrumbs className="h-auto grow shrink-0 basis-0">
        <Avatar variant="neutral">
          {currentStage.stage_number.toString()}
        </Avatar>
        <AtomicTooltip tooltipContent={currentStage.name}>
          <Breadcrumbs.Item>{`Stage: ${currentStage.name}`}</Breadcrumbs.Item>
        </AtomicTooltip>
        <Breadcrumbs.Divider />
        <Avatar variant="neutral">{step.step_number.toString()}</Avatar>
        <AtomicTooltip tooltipContent={step.name}>
          <Breadcrumbs.Item active={true}>
            {`Step: ${step.name}`}
          </Breadcrumbs.Item>
        </AtomicTooltip>
      </Breadcrumbs>,
      <UpgradeStepSidebar
        showExampleData={showExampleData}
        mode={mode}
        upgrade={upgradePlan}
        stage={currentStage}
        step={step}
      />,
    );
  };

  const handleStepAgentDrawerOpen = (step: UpgradeStageStep) => {
    setRightDrawerContents(
      <Breadcrumbs className="h-auto grow shrink-0 basis-0">
        <Avatar variant="neutral">
          {currentStage.stage_number.toString()}
        </Avatar>
        <AtomicTooltip tooltipContent={currentStage.name}>
          <Breadcrumbs.Item>{`Stage: ${currentStage.name}`}</Breadcrumbs.Item>
        </AtomicTooltip>
        <Breadcrumbs.Divider />
        <Avatar variant="neutral">{step.step_number.toString()}</Avatar>
        <AtomicTooltip tooltipContent={step.name}>
          <Breadcrumbs.Item active={true}>
            {`Step: ${step.name}`}
          </Breadcrumbs.Item>
        </AtomicTooltip>
      </Breadcrumbs>,
      <UpgradeStepAgentSidebar
        showExampleData={showExampleData}
        mode={mode}
        upgrade={upgradePlan}
        stage={currentStage}
        step={step}
      />,
    );
  };

  return (
    <Page title={'Upgrade Details'}>
      <div className={styles['pageContents']} style={{ marginLeft: '32px' }}>
        <Breadcrumbs>
          <Breadcrumbs.Item onClick={() => navigate(basePath)}>
            Upgrade Copilot
          </Breadcrumbs.Item>
          <Breadcrumbs.Divider name="FeatherChevronRight" />
          <Breadcrumbs.Item onClick={() => navigate(basePath)}>
            {getUpgradeModeHumanReadable(mode) + 's'}
          </Breadcrumbs.Item>
          <Breadcrumbs.Divider name="FeatherChevronRight" />
          {breadcrumb && (
            <>
              <Breadcrumbs.Item
                data-cy={`${convertToLowerCaseWithDashes(
                  `breadcrumb-${breadcrumb
                    ?.replace(/ /g, '')
                    .replace(/&/g, '')
                    .toLowerCase()}`,
                )}`}
                onClick={() => navigate(basePath)}
              >
                {breadcrumb}
              </Breadcrumbs.Item>
              <Breadcrumbs.Divider name="FeatherChevronRight" />
            </>
          )}
          <Breadcrumbs.Item data-cy="upgrade-plan-name" active={true}>
            {upgradePlan.name}
          </Breadcrumbs.Item>
        </Breadcrumbs>
        <div className={styles['stack-838c97b7']}>
          <div className={styles['stack']}>
            <div className={styles['stack-400934b1']}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <span className="text-section-header font-section-header text-default-font">
                  {upgradePlan.name}
                </span>
                <IconButton
                  icon="FeatherActivity"
                  onClick={() => {
                    if (mode === UpgradeViewMode.Assessment) {
                      setRightDrawerContents(
                        `Assessment: ${upgradePlan.name}`,
                        <UpgradeSidebar
                          upgrade={upgradePlan}
                          stages={stages}
                          mode={mode}
                          showExampleData={showExampleData}
                        />,
                      );
                    } else {
                      setRightDrawerContents(
                        `Upgrade: ${upgradePlan.name}`,
                        <UpgradeSidebar
                          upgrade={upgradePlan}
                          stages={stages}
                          mode={mode}
                          showExampleData={showExampleData}
                        />,
                      );
                    }
                  }}
                />
              </div>
              <div className={styles['stack-18c91f9b']}>
                <span className={styles['text']}>ID:</span>
                <span className={styles['text']}>{upgradePlan.id}</span>
                <ClickToCopy
                  textToCopy={upgradePlan.id}
                  className="text-subtext-color"
                />
              </div>
              {upgradePlan.requested_by && upgradePlan.created && (
                <div className={styles['stack-18c91f9b']}>
                  <span className={styles['text']}>Requested by:</span>
                  <AtomicTooltip
                    tooltipContent={getTooltipForUpgradeRequestedBy(
                      upgradePlan.requested_by?.name,
                      upgradePlan.created,
                      UpgradeViewMode.Plan,
                    )}
                    className="ph-no-capture"
                  >
                    <span
                      className={classNames(styles['text'], 'ph-no-capture')}
                    >
                      {upgradePlan.requested_by?.name || ''}
                    </span>
                  </AtomicTooltip>
                </div>
              )}
              {additionalHeaderElements}
            </div>
            {tabIdx !== 0 && (
              <Button
                size="large"
                variant="brand-tertiary"
                icon="FeatherArrowLeft"
                onClick={() => {
                  setTabIdx(tabIdx - 1);
                  logEvent('upgrade-plan-previous-stage-clicked', {
                    upgrade_plan_id: upgradePlan.id,
                    navigatedFromStageName: stages[tabIdx].name,
                    currentStageName: stages[tabIdx - 1].name,
                  });
                }}
              ></Button>
            )}
            {tabIdx !== stages.length - 1 && (
              <Button
                size="large"
                variant="brand-tertiary"
                icon="FeatherArrowRight"
                onClick={() => {
                  setTabIdx(tabIdx + 1);
                  logEvent('upgrade-plan-next-stage-clicked', {
                    upgrade_plan_id: upgradePlan.id,
                    navigatedFromStageName: stages[tabIdx].name,
                    currentStageName: stages[tabIdx + 1].name,
                  });
                }}
              ></Button>
            )}
            {!!actions && actions}
          </div>
        </div>
        {showExampleData && (
          <Alert
            variant="warning"
            title="You are currently viewing example data"
            description=""
          />
        )}
        {upgradePlan.status === 'regenerate-in-progress' && (
          <Alert
            variant="warning"
            title="This upgrade plan is currently being regenerated."
            description=""
          />
        )}

        {upgradePlan.header !== undefined && (
          <InformationalHeader>
            {upgradePlan.header.items.map((item) => (
              <InformationalHeader.Item
                key={item.title}
                title={item.title}
                icon={
                  item.icon && (
                    <Icon
                      style={{ color: 'var(--subtext-color)' }}
                      name={item.icon as SubframeCore.IconName}
                    />
                  )
                }
                bodySlot={
                  // NOTE: this is a hack, until we find a way to get header items style from json
                  item.title === 'Env' ? (
                    <Badge variant="neutral">{item.value}</Badge>
                  ) : (
                    <span className="text-body-bold font-body-bold text-default-font">
                      {item.value}
                    </span>
                  )
                }
              />
            ))}
          </InformationalHeader>
        )}

        <Steps>
          {stages.map((stage, index) => {
            return (
              <Steps.Step
                key={stage.name}
                name={stage.name}
                firstStep={index === 0}
                lastStep={index === stages.length - 1}
                variant={getStageVariant(stage, tabIdx, index)}
                stepNumber={
                  stage.icon === undefined
                    ? stage.stage_number.toString()
                    : undefined
                }
                iconName={
                  stage.icon === undefined
                    ? undefined
                    : (stage.icon as SubframeCore.IconName)
                }
                onClick={() => {
                  setTabIdx(index);
                  logEvent('upgrade-plan-stage-clicked', {
                    upgrade_plan_id: upgradePlan.id,
                    stageName: stage.name,
                    navigatedFromStage: stages[tabIdx]?.name,
                  });
                }}
              />
            );
          })}
        </Steps>

        <>
          {currentStage.header_content_blocks?.map((block, idx) => (
            <RenderBlock
              key={idx}
              block={block}
              context={{
                account: account,
                subscriptions: subscriptions,
                upgrade: upgradePlan,
                mode: mode,
                exampleData: showExampleData,
                stage: currentStage,
                stageIdx: tabIdx,
                moveToNextStage: () => {
                  setTabIdx((tabIdx + 1) % stages.length);
                  window.scrollTo(0, 0);
                  logEvent('upgrade-plan-next-stage-clicked', {
                    upgrade_plan_id: upgradePlan.id,
                    navigatedFromStageName: currentStage.name,
                    stageName: stages[(tabIdx + 1) % stages.length].name,
                  });
                },
                navigateToRequestTemplate: () => {
                  logEvent('upgrade-assessment-move-to-create-template', {
                    upgrade_plan_id: upgradePlan.id,
                    navigateFromStageName: currentStage.name,
                  });
                  navigate(upgradeTemplateRequestPath);
                },
                navigate: (url: string) => {
                  navigate(accountBasePath + url);
                },
                openFeedbackDialog: (
                  _vote: 'up' | 'down',
                  _row: number,
                  _column: number,
                ) => {},
              }}
              onChange={onChange}
            />
          ))}
          {currentStage.status === 'blocked' && (
            <Alert
              variant="warning"
              title="This stage is currently locked. Complete the prior stages before executing this stage"
              description=""
              className={styles.fullWidth}
            />
          )}

          {currentStage.steps.length < 1 &&
            currentStage.header_content_blocks?.length === 0 &&
            currentStage.footer_content_blocks?.length === 0 && (
              <Alert
                variant="warning"
                title="This stage is not available yet"
                description="We will notify you when this stage is available for execution"
              />
            )}

          <div className={styles['crossAxisStretch']}>
            {currentStage.steps
              .sort((a, b) => a.step_number - b.step_number)
              .map((step, index) => {
                const key = `${step.step_number}-${tabIdx}`;
                const aiPullRequestActionAvailable =
                  mode === UpgradeViewMode.Plan &&
                  step.allowed_action_types?.includes('open-lcm-agent');

                return (
                  <StepBase
                    // NOTE: we want to force a remount whenever stage changes so that accordion state is reset
                    key={key}
                    stepTitle={step.name}
                    stepBody={step.description}
                    stepNumber={step.step_number.toString()}
                    lastStep={index === stages[tabIdx].steps.length - 1}
                    iconName={
                      step.icon === undefined
                        ? undefined
                        : (step.icon as SubframeCore.IconName)
                    }
                    variant={
                      step.status === 'completed'
                        ? 'success'
                        : step.status === 'skipped'
                        ? 'skipped'
                        : 'default'
                    }
                    defaultOpen={
                      stagesStepsDefaultOpen ||
                      stages[tabIdx].status !== 'completed'
                    }
                    open={
                      stepOpen[key] === undefined
                        ? stagesStepsDefaultOpen
                        : stepOpen[key]
                    }
                    onOpenChange={(open) => {
                      setStepOpen((prev) => {
                        const next = { ...prev };
                        next[key] = open;
                        return next;
                      });
                      if (open) {
                        logEvent('upgrade-plan-step-viewed', {
                          upgrade_plan_id: upgradePlan.id,
                          stageName: currentStage.name,
                          stepName: step.name,
                          stepNumber: step.step_number,
                          stepStatus: step.status,
                        });
                      }
                    }}
                    badges={
                      <>
                        {aiPullRequestActionAvailable && (
                          <AtomicTooltip tooltipContent="AI support available">
                            <SubframeCore.Icon
                              className="text-subheader font-subheader text-brand-primary"
                              name="FeatherSparkles"
                            />
                          </AtomicTooltip>
                        )}
                        {(step.badges || []).length === 0 ? undefined : (
                          <>
                            {(step.badges || [])
                              .map((badge) => ({
                                ...badge,
                                text: badge.text.trim(),
                              }))
                              .filter((badge) => badge.text !== '')
                              .map((badge, idx) => {
                                return (
                                  <Badge
                                    key={idx}
                                    variant={getBadgeVariant(badge.variant)}
                                  >
                                    {badge.text}
                                  </Badge>
                                );
                              })}
                          </>
                        )}
                        {step.requirement_level === 'required' && (
                          <Badge variant="brand">Required</Badge>
                        )}
                        {step.requirement_level === 'optional' && (
                          <Badge variant="neutral">Optional</Badge>
                        )}
                        {step.status === 'marked-for-upgrade' && (
                          <CommentTooltip
                            userName={
                              step.last_status_change_details?.user?.name
                            }
                            picture={
                              step.last_status_change_details?.user?.picture
                            }
                            comment={
                              step?.last_status_change_details?.comment || ''
                            }
                            time={step?.last_status_change_details?.changed}
                            action={"marked this step as 'For Upgrade'"}
                          >
                            <Badge variant="brand">Marked for Upgrade</Badge>
                          </CommentTooltip>
                        )}
                        {step.status === 'not-marked-for-upgrade' && (
                          <CommentTooltip
                            userName={
                              step.last_status_change_details?.user?.name
                            }
                            picture={
                              step.last_status_change_details?.user?.picture
                            }
                            time={step?.last_status_change_details?.changed}
                            comment={
                              step?.last_status_change_details?.comment || ''
                            }
                            action={"marked this step as 'Don't Upgrade'"}
                          >
                            <Badge variant="neutral">
                              Not Marked for Upgrade
                            </Badge>
                          </CommentTooltip>
                        )}
                        {step.status === 'skipped' &&
                          step.last_status_change_details?.reason !==
                            undefined && (
                            <CommentTooltip
                              userName={
                                step.last_status_change_details?.user?.name
                              }
                              picture={
                                step.last_status_change_details?.user?.picture
                              }
                              comment={
                                step?.last_status_change_details?.comment || ''
                              }
                              time={step?.last_status_change_details?.changed}
                            >
                              <Badge variant="neutral">
                                {`Skipped: ${getSkipReasonHuman(
                                  step.last_status_change_details.reason,
                                )}`}
                              </Badge>
                            </CommentTooltip>
                          )}
                        {step.notes?.content !== undefined &&
                          step.notes?.content !== '' && (
                            <AtomicTooltip
                              tooltipContent={
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <Avatar
                                    size="x-small"
                                    image={step.notes?.user?.picture}
                                    style={{ width: '16px', height: '16px' }}
                                    className="ph-no-capture"
                                  />
                                  <span className="ph-no-capture">
                                    Last updated by {step.notes?.user?.name}{' '}
                                    {formatDistanceToNow(
                                      new Date(step.notes?.updated * 1000),
                                      { addSuffix: true },
                                    )}
                                  </span>
                                </div>
                              }
                            >
                              <IconWithBackground icon="FeatherEdit2" />
                            </AtomicTooltip>
                          )}
                        {step.comments.latest !== undefined &&
                          step.comments.latest?.comment !== '' && (
                            <CommentTooltip
                              userName={step.comments.latest?.user?.name}
                              picture={step.comments.latest?.user?.picture}
                              comment={step?.comments.latest?.comment}
                              time={step?.comments.latest?.time}
                              action={
                                step.comments.latest?.is_for_chkk
                                  ? 'left comment for Chkk'
                                  : ''
                              }
                            >
                              {step.comments.latest?.is_for_chkk && (
                                <Avatar
                                  image={EVA_AVATAR_IMAGE}
                                  size="x-small"
                                />
                              )}
                            </CommentTooltip>
                          )}
                      </>
                    }
                    actionButtons={
                      <div
                        style={{
                          display: 'inherit',
                          gap: 'inherit',
                        }}
                        onClick={(event) => {
                          // need to stop propagation to avoid odd accordion expand/contract actionButtons
                          // (as clicking on sub-elements inside this div is meant to not trigger those)
                          event.stopPropagation();

                          if (showExampleData) {
                            enqueueSnackbar(
                              'This is a demonstration view only at this point - action buttons are disabled in preview mode',
                              {
                                variant: 'warning',
                                autoHideDuration: toastAutoHideDuration,
                              },
                            );
                          }
                        }}
                      >
                        <Actions
                          sections={[
                            {
                              title: 'Lifecycle Actions',
                              items: [
                                <>
                                  {step.status === 'skipped' ? (
                                    <Actions.Item
                                      key="undo-skip"
                                      title="Undo Skip"
                                      icon="FeatherCornerUpRight"
                                      disabled={
                                        showExampleData ||
                                        isPlanActionDisabled(
                                          upgradePlan.status,
                                        ) ||
                                        !step.allowed_action_types?.includes(
                                          'undo-skip',
                                        )
                                      }
                                      tooltip={tooltipForAction(
                                        step,
                                        'undo-skip',
                                      )}
                                      onClick={() => {
                                        setDialogOpen('undo-skip');
                                        setSelectedStep(step);
                                      }}
                                    />
                                  ) : (
                                    <Actions.Item
                                      key="skip"
                                      title="Skip"
                                      icon="FeatherSkipForward"
                                      disabled={
                                        showExampleData ||
                                        isPlanActionDisabled(
                                          upgradePlan.status,
                                        ) ||
                                        !step.allowed_action_types?.includes(
                                          'skip',
                                        ) ||
                                        step.status === 'completed'
                                      }
                                      tooltip={
                                        step.status === 'completed'
                                          ? 'Action is not available for completed steps'
                                          : tooltipForAction(step, 'skip')
                                      }
                                      onClick={() => {
                                        setDialogOpen('skip');
                                        setSelectedStep(step);
                                      }}
                                    />
                                  )}
                                </>,
                                <>
                                  {step.status === 'completed' ? (
                                    <Actions.Item
                                      key="undo-mark-as-done"
                                      title="Undo Mark as Done"
                                      icon="FeatherUndo"
                                      disabled={
                                        showExampleData ||
                                        mode === UpgradeViewMode.Template ||
                                        !step.allowed_action_types?.includes(
                                          'undo-mark-completed',
                                        ) ||
                                        isPlanActionDisabled(upgradePlan.status)
                                      }
                                      tooltip={tooltipForAction(
                                        step,
                                        'undo-mark-completed',
                                      )}
                                      onClick={async () => {
                                        try {
                                          await updateUpgradePlanStageStep(
                                            upgradePlan.id,
                                            currentStage.stage_number.toString(),
                                            step.step_number.toString(),
                                            { status: 'in-progress' },
                                            {
                                              headers: {
                                                Authorization: `Bearer ${account.token}`,
                                              },
                                            },
                                          );

                                          onChange(true, true);
                                        } catch (err) {
                                          Sentry.captureException(err);
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Actions.Item
                                      key="mark-as-done"
                                      title="Mark as Done"
                                      icon="FeatherCheck"
                                      disabled={
                                        showExampleData ||
                                        mode === UpgradeViewMode.Template ||
                                        step.status === 'skipped' ||
                                        !step.allowed_action_types?.includes(
                                          'mark-completed',
                                        ) ||
                                        isPlanActionDisabled(upgradePlan.status)
                                      }
                                      tooltip={
                                        step.status === 'skipped'
                                          ? 'Action is not available for skipped steps'
                                          : tooltipForAction(
                                              step,
                                              'mark-completed',
                                            )
                                      }
                                      onClick={async () => {
                                        try {
                                          await updateUpgradePlanStageStep(
                                            upgradePlan.id,
                                            currentStage.stage_number.toString(),
                                            step.step_number.toString(),
                                            { status: 'completed' },
                                            {
                                              headers: {
                                                Authorization: `Bearer ${account.token}`,
                                              },
                                            },
                                          );

                                          onChange(true, true);
                                        } catch (err) {
                                          Sentry.captureException(err);
                                        }
                                      }}
                                    />
                                  )}
                                  <Actions.Item
                                    key="export-markdown"
                                    title="Export as Markdown"
                                    icon="FeatherCode"
                                    disabled={showExampleData}
                                    onClick={async () => {
                                      try {
                                        let resp = undefined;
                                        if (
                                          mode === UpgradeViewMode.Assessment
                                        ) {
                                          resp =
                                            await getUpgradeAssessmentStageStep(
                                              upgradePlan.id,
                                              `${currentStage.stage_number}`,
                                              `${step.step_number}`,
                                              {
                                                headers: {
                                                  Authorization: `Bearer ${account.token}`,
                                                  Accept: 'text/markdown',
                                                },
                                                responseType: 'text',
                                              },
                                            );
                                        } else if (
                                          mode === UpgradeViewMode.Template
                                        ) {
                                          resp =
                                            await getUpgradeTemplateStageStep(
                                              upgradePlan.id,
                                              `${currentStage.stage_number}`,
                                              `${step.step_number}`,
                                              {
                                                headers: {
                                                  Authorization: `Bearer ${account.token}`,
                                                  Accept: 'text/markdown',
                                                },
                                                responseType: 'text',
                                              },
                                            );
                                        } else if (
                                          mode === UpgradeViewMode.Plan
                                        ) {
                                          resp = await getUpgradePlanStageStep(
                                            upgradePlan.id,
                                            `${currentStage.stage_number}`,
                                            `${step.step_number}`,
                                            {
                                              headers: {
                                                Authorization: `Bearer ${account.token}`,
                                                Accept: 'text/markdown',
                                              },
                                              responseType: 'text',
                                            },
                                          );
                                        }

                                        copy(
                                          (resp as unknown as { text: string })
                                            .text,
                                        );
                                        enqueueSnackbar(
                                          'Copied Markdown to Clipboard!',
                                          {
                                            variant: 'success',
                                            autoHideDuration:
                                              toastAutoHideDuration,
                                          },
                                        );
                                      } catch (error) {
                                        Sentry.captureException(error);
                                        enqueueSnackbar(
                                          somethingWentWrong.replace(
                                            '<action>',
                                            'exporting the step as Markdown',
                                          ),
                                          {
                                            variant: 'error',
                                            autoHideDuration:
                                              toastAutoHideDuration,
                                          },
                                        );
                                      }
                                    }}
                                  />
                                </>,
                              ],
                            },
                            {
                              title: 'Agent Actions',
                              hidden: !aiPullRequestActionAvailable,
                              items: [
                                <Actions.Item
                                  key="open-lcm-agent"
                                  title="Open IaC Pull Request with AI"
                                  icon="FeatherSparkles"
                                  className={
                                    aiPullRequestActionAvailable ? '' : 'hidden'
                                  }
                                  onClick={() =>
                                    handleStepAgentDrawerOpen(step)
                                  }
                                />,
                              ],
                            },
                            {
                              title: 'Workflow Actions',
                              items: [
                                <Actions.Item
                                  key="open-upgrade-plan"
                                  title="Open Upgrade Plan"
                                  icon="FeatherArrowUpRight"
                                  className={
                                    step.allowed_action_types?.includes(
                                      'open-upgrade-plan',
                                    ) && step.description?.startsWith('upgr')
                                      ? ''
                                      : 'hidden'
                                  }
                                  link={`${upgradeAddonBasePath}/${
                                    step.description?.startsWith('upgr')
                                      ? step.description
                                      : ''
                                  }`}
                                />,
                                <Actions.Item
                                  key="rerun-checks"
                                  title="Re-run Checks"
                                  icon="FeatherRefreshCcw"
                                  className={
                                    step.allowed_action_types?.includes(
                                      'rerun-checks',
                                    )
                                      ? ''
                                      : 'hidden'
                                  }
                                  onClick={async () => {
                                    try {
                                      await updateUpgradePlanStageStep(
                                        upgradePlan.id,
                                        currentStage.stage_number.toString(),
                                        step.step_number.toString(),
                                        {
                                          health_checks: {
                                            aggregate_status: 'pending',
                                          },
                                        },
                                        {
                                          headers: {
                                            Authorization: `Bearer ${account.token}`,
                                          },
                                        },
                                      );

                                      onChange(true, true);
                                    } catch (err) {
                                      Sentry.captureException(err);
                                    }
                                  }}
                                  disabled={
                                    showExampleData ||
                                    isPlanActionDisabled(upgradePlan.status)
                                  }
                                />,
                                <Actions.Item
                                  key="add-before"
                                  title="Add Step Before"
                                  icon="FeatherArrowUp"
                                  onClick={() => {
                                    setSelectedStep(step);
                                    setAddStepPosition('before');
                                    setDialogOpen('create');
                                    logEvent('step-action-add-step-before', {
                                      upgradeId: upgradePlan.id,
                                      stageNumber: currentStage.stage_number,
                                      stepNumber: step.step_number,
                                    });
                                  }}
                                  disabled={
                                    showExampleData ||
                                    isPlanActionDisabled(upgradePlan.status)
                                  }
                                  tooltip={tooltipForAction(step)}
                                />,
                                <Actions.Item
                                  key="add-after"
                                  title="Add Step After"
                                  icon="FeatherArrowDown"
                                  disabled={
                                    showExampleData ||
                                    isPlanActionDisabled(upgradePlan.status)
                                  }
                                  tooltip={tooltipForAction(step)}
                                  onClick={() => {
                                    setSelectedStep(step);
                                    setAddStepPosition('after');
                                    setDialogOpen('create');
                                    logEvent('step-action-add-step-after', {
                                      upgradeId: upgradePlan.id,
                                      stageNumber: currentStage.stage_number,
                                      stepNumber: step.step_number,
                                    });
                                  }}
                                />,
                                <Actions.Item
                                  key="edit"
                                  title="Edit Step"
                                  icon="FeatherEdit2"
                                  onClick={() => {
                                    setSelectedStep(step);
                                    setDialogOpen('edit');
                                    logEvent('step-action-edit-step', {
                                      upgradeId: upgradePlan.id,
                                      stageNumber: currentStage.stage_number,
                                      stepNumber: step.step_number,
                                    });
                                  }}
                                  disabled={
                                    step.owner !== 'customer' ||
                                    isPlanActionDisabled(upgradePlan.status) ||
                                    showExampleData
                                  }
                                  tooltip={
                                    step.owner !== 'customer'
                                      ? 'Action is available for custom steps only'
                                      : tooltipForAction(step)
                                  }
                                />,
                                <Actions.Item
                                  key="delete"
                                  title="Delete Step"
                                  icon="FeatherTrash"
                                  onClick={() => {
                                    setSelectedStep(step);
                                    setDialogOpen('delete');
                                    logEvent('step-action-delete-step', {
                                      upgradeId: upgradePlan.id,
                                      stageNumber: currentStage.stage_number,
                                      stepNumber: step.step_number,
                                    });
                                  }}
                                  disabled={
                                    showExampleData ||
                                    step.owner !== 'customer' ||
                                    isPlanActionDisabled(upgradePlan.status)
                                  }
                                  tooltip={
                                    step.owner !== 'customer'
                                      ? 'Action is available for custom steps only'
                                      : tooltipForAction(step)
                                  }
                                />,
                                <>
                                  {step.owner === 'chkk' && (
                                    <Actions.Item
                                      key="preverify"
                                      title="Preverify"
                                      icon="FeatherShieldCheck"
                                      disabled={true}
                                      preview={true}
                                    />
                                  )}
                                </>,
                              ],
                            },
                            {
                              title: 'Activity',
                              items: [
                                <Actions.Item
                                  key="activity"
                                  title="View Activity"
                                  icon="FeatherActivity"
                                  onClick={() => {
                                    handleStepActivityDrawerOpen(step);
                                  }}
                                />,
                                <Actions.Item
                                  key="comment-team"
                                  title="Leave Comment for Team"
                                  icon="FeatherMessageCircle"
                                  onClick={() => {
                                    handleStepActivityDrawerOpen(step);
                                  }}
                                  disabled={
                                    showExampleData ||
                                    isPlanActionDisabled(upgradePlan.status)
                                  }
                                  tooltip={tooltipForAction(step)}
                                />,
                                <Actions.Item
                                  key="comment-chkk"
                                  title="Leave Comment for Chkk"
                                  icon="FeatherMessageSquare"
                                  onClick={() => {
                                    handleStepActivityDrawerOpen(step);
                                  }}
                                  disabled={
                                    showExampleData ||
                                    isPlanActionDisabled(upgradePlan.status)
                                  }
                                  tooltip={tooltipForAction(step)}
                                />,
                              ],
                            },
                          ]}
                        />
                        <IconButton
                          icon="FeatherActivity"
                          size="medium"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleStepActivityDrawerOpen(step);
                          }}
                        />
                      </div>
                    }
                  >
                    {(step.content_blocks || []).length < 1 && <></>}
                    {step.content_blocks?.map((block, blockIdx) => {
                      return (
                        <RenderBlock
                          key={blockIdx}
                          block={block}
                          context={{
                            account: account,
                            subscriptions: subscriptions,
                            upgrade: upgradePlan,
                            mode: mode,
                            exampleData: showExampleData,
                            stage: currentStage,
                            stageIdx: tabIdx,
                            step: step,
                            stepIdx: index,
                            moveToNextStage: () => {
                              setTabIdx((tabIdx + 1) % stages.length);
                              window.scrollTo(0, 0);
                              logEvent(
                                'upgrade-plan-move-to-next-stage-clicked',
                                {
                                  upgrade_plan_id: upgradePlan.id,
                                  navigatedFromStageName: currentStage.name,
                                  stageName:
                                    stages[(tabIdx + 1) % stages.length]?.name,
                                },
                              );
                            },
                            navigateToRequestTemplate: () => {
                              logEvent(
                                'upgrade-assessment-move-to-create-template',
                                {
                                  upgrade_plan_id: upgradePlan.id,
                                  navigateFromStageName: currentStage.name,
                                },
                              );
                              navigate(upgradeTemplateRequestPath);
                            },
                            navigate: (url: string) => {
                              navigate(accountBasePath + url);
                            },
                            openFeedbackDialog: (
                              vote: 'up' | 'down',
                              row: number,
                              column: number,
                            ) => {
                              setSelectedTableCell({ row, column });
                              setDialogOpen(
                                vote === 'up'
                                  ? 'provide-feedback-up'
                                  : 'provide-feedback-down',
                              );
                              setSelectedStep(step);
                            },
                          }}
                          onChange={onChange}
                        />
                      );
                    })}

                    {(step.checks || []).length > 0 && (
                      <Stack>
                        <Table
                          header={
                            <Table.HeaderRow>
                              <Table.HeaderCell>Check</Table.HeaderCell>
                              <Table.HeaderCell>
                                Health Check Status
                              </Table.HeaderCell>
                            </Table.HeaderRow>
                          }
                        >
                          {step.checks?.map((check, i) => {
                            return (
                              <Table.Row key={i} clickable={true}>
                                <Table.Cell>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    className="gap-2"
                                  >
                                    <SubframeCore.Text variant="label">
                                      {check.label}
                                    </SubframeCore.Text>
                                    {check.label_info_tooltip && (
                                      <AtomicTooltip
                                        tooltipContent={
                                          check.label_info_tooltip
                                        }
                                      >
                                        <SubframeCore.Icon name="FeatherInfo" />
                                      </AtomicTooltip>
                                    )}
                                  </Stack>
                                </Table.Cell>
                                <Table.Cell>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    className="gap-2"
                                  >
                                    {check.status === 'passed' && (
                                      <SubframeCore.Icon
                                        name="FeatherCheck"
                                        style={{
                                          color: 'var(--success-800)',
                                        }}
                                      />
                                    )}
                                    {check.status === 'failed' && (
                                      <SubframeCore.Icon
                                        name="FeatherX"
                                        style={{ color: 'var(--error-800)' }}
                                      />
                                    )}
                                    {check.status === 'skipped' && (
                                      <SubframeCore.Icon
                                        name="FeatherSkipForward"
                                        style={{
                                          color: 'var(--warning-800)',
                                        }}
                                      />
                                    )}
                                    {check.status === 'pending' && (
                                      <SubframeCore.Icon
                                        name="FeatherCircle"
                                        style={{
                                          color: 'var(--neutral-800)',
                                        }}
                                      />
                                    )}
                                    <SubframeCore.Text variant="label">
                                      {check.status_text}
                                    </SubframeCore.Text>
                                  </Stack>
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table>
                      </Stack>
                    )}
                    {step.owner === 'customer' && (
                      <UpgradePlanStepMarkdown
                        content={step.notes?.content || ''}
                      />
                    )}
                  </StepBase>
                );
              })}
          </div>

          <div className={styles['stack-96df2aa1']}>
            {currentStage.footer_content_blocks?.map((block, idx) => (
              <RenderBlock
                key={idx}
                block={block}
                context={{
                  account: account,
                  subscriptions: subscriptions,
                  upgrade: upgradePlan,
                  mode: mode,
                  exampleData: showExampleData,
                  stage: currentStage,
                  stageIdx: tabIdx,
                  moveToNextStage: () => {
                    setTabIdx((tabIdx + 1) % stages.length);
                    window.scrollTo(0, 0);
                    logEvent('upgrade-plan-move-to-next-stage-clicked', {
                      upgrade_plan_id: upgradePlan.id,
                      navigatedFromStageName: currentStage.name,
                      stageName: stages[(tabIdx + 1) % stages.length]?.name,
                    });
                  },
                  navigateToRequestTemplate: () => {
                    logEvent('upgrade-assessment-move-to-create-template', {
                      upgrade_plan_id: upgradePlan.id,
                      navigateFromStageName: currentStage.name,
                    });
                    navigate(upgradeTemplateRequestPath);
                  },
                  navigate: (url: string) => {
                    navigate(accountBasePath + url);
                  },
                  openFeedbackDialog: (
                    _vote: 'up' | 'down',
                    _row: number,
                    _column: number,
                  ) => {},
                }}
                onChange={onChange}
              />
            ))}

            {/* Footer CTA card to next stage (Plan only) */}
            {mode == UpgradeViewMode.Plan &&
              currentStage.status === 'completed' && (
                <CtaCard
                  onClick={() => {
                    // click does nothing on last stage
                    if (tabIdx !== stages.length - 1) {
                      setTabIdx((tabIdx + 1) % stages.length);
                      window.scrollTo(0, 0);
                      logEvent('upgrade-plan-stage-footer-clicked', {
                        upgrade_plan_id: upgradePlan.id,
                        navigatedFromStageName: currentStage.name,
                        stageName: stages[(tabIdx + 1) % stages.length]?.name,
                      });
                    }
                  }}
                  className="h-auto w-full flex-none"
                  leftSlot={
                    <IconWithBackground
                      variant="success"
                      size="medium"
                      icon="FeatherCheck"
                    />
                  }
                >
                  <span className="text-subheader font-subheader text-default-font">
                    {tabIdx === stages.length - 1 &&
                    getPlanStagesCompleted(stages)
                      ? `🎉 You have completed all the upgrade steps`
                      : `🎉 You are all caught up on ${currentStage?.name}`}
                  </span>
                  <span className="text-body font-body text-default-font">
                    {tabIdx !== stages.length - 1
                      ? `Let's proceed to ${
                          stages[(tabIdx + 1) % stages.length]?.name
                        } now`
                      : ''}
                  </span>
                </CtaCard>
              )}
          </div>
        </>
        <MarkUpgradePlanAsCompletedDialog
          open={openMarkAsCompleted}
          onClose={() => setOpenMarkAsCompleted(false)}
          mutatePlan={() => onChange(false, true)}
          upgradeId={upgradePlan.id}
        />
        {selectedStep && (
          <>
            {mode === UpgradeViewMode.Template ? (
              <>
                <UpgradeTemplateSkipStepDialog
                  token={account?.token || ''}
                  open={dialogOpen === 'skip'}
                  setOpen={(bool) => setDialogOpen(bool ? 'skip' : undefined)}
                  upgradeId={upgradePlan.id}
                  stageNumber={currentStage.stage_number}
                  stepNumber={selectedStep.step_number}
                  onSuccess={async () => {
                    await onChange(true, false);
                    setDialogOpen(undefined);
                  }}
                />

                <UpgradeTemplatesUndoSkipStepDialog
                  account={account || ({} as Account)}
                  upgrade={upgradePlan as UpgradePlan}
                  stageNumber={currentStage.stage_number}
                  stepNumber={selectedStep.step_number}
                  onSuccess={() => {
                    onChange(true, false);
                    setDialogOpen(undefined);
                  }}
                  open={dialogOpen === 'undo-skip'}
                  setOpen={(bool) => {
                    if (bool) {
                      setDialogOpen('undo-skip');
                    } else {
                      setDialogOpen(undefined);
                    }
                  }}
                />
              </>
            ) : (
              <>
                <UpgradesSkipStepDialog
                  open={dialogOpen === 'skip'}
                  setOpen={(bool) => setDialogOpen(bool ? 'skip' : undefined)}
                  mode={mode}
                  account={account}
                  upgrade={upgradePlan}
                  stageNumber={currentStage.stage_number}
                  stepNumber={selectedStep.step_number}
                  onSuccess={() => {
                    onChange(true, false);
                    logEvent('upgrade-plan-step-skipped', {
                      upgrade_plan_id: upgradePlan.id,
                      stageName: currentStage.name,
                      stepName: selectedStep.name,
                      stepNumber: selectedStep.step_number,
                    });
                  }}
                />
                <UpgradesUndoSkipStepDialog
                  open={dialogOpen === 'undo-skip'}
                  setOpen={(bool) =>
                    setDialogOpen(bool ? 'undo-skip' : undefined)
                  }
                  mode={mode}
                  account={account}
                  upgrade={upgradePlan}
                  stageNumber={currentStage.stage_number}
                  stepNumber={selectedStep.step_number}
                  onSuccess={() => {
                    onChange(true, false);
                    logEvent('upgrade-plan-step-unskipped', {
                      upgrade_plan_id: upgradePlan.id,
                      stageName: currentStage.name,
                      stepName: selectedStep.name,
                      stepNumber: selectedStep.step_number,
                    });
                  }}
                />
              </>
            )}
            <UpgradeFeedbackDialog
              mode={mode}
              open={
                dialogOpen === 'provide-feedback-up' ||
                dialogOpen === 'provide-feedback-down'
              }
              setOpen={() => setDialogOpen(undefined)}
              onConfirm={async () => {
                onChange(true, false);
              }}
              upgradeId={upgradePlan.id}
              stageNumber={currentStage.stage_number}
              step={selectedStep}
              rowNumber={selectedTableCell.row || 0}
              columnNumber={selectedTableCell.column || 0}
              vote={dialogOpen === 'provide-feedback-up' ? 'up' : 'down'}
            />
            <UpgradesDeleteStepDialog
              mode={mode}
              open={dialogOpen === 'delete'}
              setOpen={(bool) => setDialogOpen(bool ? 'delete' : undefined)}
              onConfirm={async () => {
                await onChange(true, false);
                setDialogOpen(undefined);
              }}
              upgradeId={upgradePlan.id}
              stageNumber={currentStage.stage_number}
              step={selectedStep}
            />
            <UpgradesCreateEditStepDialog
              viewMode={mode}
              open={dialogOpen === 'create' || dialogOpen === 'edit'}
              mode={dialogOpen === 'create' ? 'create' : 'edit'}
              setOpen={() => setDialogOpen(undefined)}
              onConfirm={async () => {
                await onChange(true, false);
                setDialogOpen(undefined);
              }}
              upgradeId={upgradePlan.id}
              stageNumber={currentStage.stage_number}
              step={selectedStep}
              addStepPosition={addStepPosition}
            />
          </>
        )}
      </div>
    </Page>
  );
}

interface RenderContext {
  account: Account;
  subscriptions: ListSubscriptionsResponse | undefined;
  upgrade: UpgradeAny;
  mode: UpgradeViewMode;
  exampleData: boolean;
  stage: UpgradeStage;
  stageIdx: number;
  step?: UpgradeStageStep;
  stepIdx?: number;
  moveToNextStage: () => void;
  navigateToRequestTemplate: () => void;
  navigate: (url: string) => void;
  openFeedbackDialog: (
    vote: 'up' | 'down',
    row: number,
    column: number,
  ) => void;
}

function renderTableCell(
  row: number,
  col: number,
  cell: UpgradeContentBlockTableDataCell,
  context: RenderContext,
): JSX.Element {
  switch (cell.style) {
    case 'markdown': {
      return (
        <span>
          <UpgradePlanStepMarkdown
            content={cell.value || ''}
            defaultTextClass="text-label"
            defaultLinkClass="text-label hover:text-label-bold"
          />
        </span>
      );
    }
    case 'normal':
    case undefined: {
      var icon: React.ReactNode | undefined = undefined;
      if (cell.icon) {
        if (cell.icon.variant === 'success' && cell.icon.background) {
          icon = <IconWithBackground variant="success" icon="FeatherCheck" />;
        } else if (cell.icon.variant === 'success' && !cell.icon.background) {
          icon = (
            <SubframeCore.Icon
              name="FeatherCheck"
              style={{ color: 'var(--success-800)' }}
            />
          );
        } else if (cell.icon.variant === 'error' && cell.icon.background) {
          icon = <IconWithBackground variant="error" icon="FeatherX" />;
        } else if (cell.icon.variant === 'error' && !cell.icon.background) {
          icon = (
            <SubframeCore.Icon
              name="FeatherX"
              style={{ color: 'var(--error-800)' }}
            />
          );
        } else if (cell.icon.variant === 'unknown' && cell.icon.background) {
          icon = (
            <IconWithBackground variant="brand" icon="FeatherShieldQuestion" />
          );
        } else if (cell.icon.variant === 'unknown' && !cell.icon.background) {
          icon = (
            <span
              style={{
                color: 'var(--neutral-400)',
                font: 'var(--body-bold)',
                fontWeight: 'bolder',
              }}
            >
              ?
            </span>
          );
        }

        if (cell.icon.tooltip) {
          icon = (
            <AtomicTooltip tooltipContent={cell.icon.tooltip.replace(/`/g, '')}>
              {icon}
            </AtomicTooltip>
          );
        }
      }

      let actions = undefined;
      if ((cell.actions || []).length === 1) {
        const action = (cell.actions || [])[0];
        let disabled = false;

        let addon_id = '';

        if (action.action_params?.project_id) {
          addon_id = (action.action_params.project_id || '').replace(
            /proj_/,
            'addon_',
          );

          if (isExampleData(context.upgrade.id)) {
            disabled = true;
          } else {
            disabled =
              (context.subscriptions?.total_entitlement?.addons || []).length >
              0
                ? (
                    context.subscriptions?.total_entitlement?.addons?.filter(
                      (a) =>
                        a.addon_id === addon_id && a.individual_upgrade_allowed,
                    ) || []
                  ).length === 0
                : false;
          }
        }

        actions = (
          <Button
            variant="brand-secondary"
            disabled={disabled}
            icon={action.icon}
            onClick={() => {
              if (disabled) {
                return;
              }

              if (action.action_type === 'request.upgrade.template') {
                const cluster =
                  (context.upgrade.resources.clusters || []).length > 0
                    ? (context.upgrade.resources.clusters || [])[0].id
                    : '';
                context.navigate(
                  `/upgrades/templates/addons/new?cluster_id=${cluster}&addon_id=${addon_id}`,
                );
              }
            }}
          >
            {action.value}
          </Button>
        );

        if (disabled) {
          if (isExampleData(context.upgrade.id)) {
            actions = (
              <AtomicTooltip tooltipContent="Action not available for example data.">
                {actions}
              </AtomicTooltip>
            );
          } else {
            actions = (
              <AtomicTooltip tooltipContent="Action not available for your current subscription. Upgrade to Business plan.">
                {actions}
              </AtomicTooltip>
            );
          }
        } else if (action.tooltip) {
          actions = (
            <AtomicTooltip tooltipContent={action.tooltip}>
              {actions}
            </AtomicTooltip>
          );
        }
      } else if ((cell.actions || []).length > 1) {
        actions = <></>;
      }

      return (
        <span className="flex h-auto gap-2">
          {cell.value && (
            <SubframeCore.Text className="flex items-center" variant="label">
              {cell.value}
            </SubframeCore.Text>
          )}
          {cell.info_text && (
            <AtomicTooltip
              tooltipContent={
                <UpgradePlanStepMarkdown
                  content={cell.info_text}
                  defaultTextClass="color-inherit"
                  defaultLinkClass="text-label-bold"
                />
              }
            >
              <SubframeCore.Icon name="FeatherInfo" />
            </AtomicTooltip>
          )}
          {icon}
          {(cell.badges || []).length > 0 && (
            <div className="flex gap-2 items-center">
              {cell.badges?.map((b, idx) => {
                const badge = (
                  <Badge key={idx} variant={b.variant} className="w-fit">
                    {b.text}
                  </Badge>
                );

                if (b.tooltip) {
                  return (
                    <AtomicTooltip
                      key={idx}
                      tooltipContent={b.tooltip.replace(/`/g, '')}
                    >
                      {badge}
                    </AtomicTooltip>
                  );
                } else {
                  return badge;
                }
              })}
            </div>
          )}
          {cell.feedback && (
            <div className="flex gap-2 items-center">
              {context.exampleData ? (
                <AtomicTooltip tooltipContent="Providing feedback is unavailable for example data.">
                  <IconButton
                    icon="FeatherThumbsUp"
                    clickable={false}
                    variant={'neutral-tertiary-disabled'}
                  />
                </AtomicTooltip>
              ) : (
                <IconButton
                  icon="FeatherThumbsUp"
                  variant={
                    cell.feedback.feedback === 'up'
                      ? 'brand-tertiary'
                      : 'neutral-tertiary-table'
                  }
                  onClick={() => {
                    context.openFeedbackDialog('up', row, col);
                  }}
                />
              )}
              {context.exampleData ? (
                <AtomicTooltip tooltipContent="Providing feedback is unavailable for example data.">
                  <IconButton
                    icon="FeatherThumbsDown"
                    clickable={false}
                    variant={'neutral-tertiary-disabled'}
                  />
                </AtomicTooltip>
              ) : (
                <IconButton
                  icon="FeatherThumbsDown"
                  variant={
                    cell.feedback.feedback === 'down'
                      ? 'brand-tertiary'
                      : 'neutral-tertiary-table'
                  }
                  onClick={() => {
                    context.openFeedbackDialog('down', row, col);
                  }}
                />
              )}
              {cell.feedback.comment !== undefined && (
                <CommentTooltip
                  userName={(cell.feedback?.identity as UserIdentity)?.name}
                  picture={(cell.feedback?.identity as UserIdentity)?.picture}
                  comment={cell.feedback?.comment}
                  time={(cell.feedback?.updated || 0) / 1000}
                  action="provided feedback"
                />
              )}
            </div>
          )}
          {cell.actions && actions}
        </span>
      );
    }
    default:
      Sentry.captureException(
        new Error(`Undefined table cell style: ${cell.style}`),
      );
      return <></>;
  }
}

function RenderBlock({
  block,
  context,
  onChange,
}: {
  block: UpgradeContentBlock;
  context: RenderContext;
  onChange: () => void;
}): JSX.Element {
  switch (block.type) {
    case 'text': {
      const { style, text } = block;
      switch (style) {
        case 'header':
          return (
            <SubframeCore.Text variant="subheader">{text}</SubframeCore.Text>
          );
        case 'normal':
        case undefined:
          return <SubframeCore.Text variant="body">{text}</SubframeCore.Text>;
        case 'subtext':
          return (
            <SubframeCore.Text variant="body" color="subtext">
              {text}
            </SubframeCore.Text>
          );
        case 'section-header':
          return (
            <SubframeCore.Text variant="section-header">
              {text}
            </SubframeCore.Text>
          );
        case 'markdown':
          return <UpgradePlanStepMarkdown content={text} />;
        default:
          Sentry.captureException(new Error(`Undefined text style: ${style}`));
          return <></>;
      }
    }
    case 'code': {
      const { style, code, language } = block;
      switch (style) {
        case 'normal':
        case undefined:
          return (
            <Diff clipboardText={code}>
              {code.split('\n').map((line, i) => {
                return <Diff.Line key={i} text={line} language={language} />;
              })}
            </Diff>
          );
        case 'diff':
          return (
            <Diff clipboardText={code}>
              {code.split('\n').map((line, i) => {
                if (line.startsWith('+')) {
                  return (
                    <Diff.Line
                      key={i}
                      text={line.replace(/^\+\s?/, '')}
                      variant="added"
                      language={language}
                    />
                  );
                } else if (line.startsWith('-')) {
                  return (
                    <Diff.Line
                      key={i}
                      text={line.replace(/^-\s?/, '')}
                      variant="deleted"
                      language={language}
                    />
                  );
                } else {
                  return <Diff.Line key={i} text={line} language={language} />;
                }
              })}
            </Diff>
          );
        case 'output':
          return (
            <div className={styles['outputCodeSection']}>
              <div className={styles['outputCode']}>
                <span className={styles['outputCodeText']}>{code}</span>
              </div>
            </div>
          );
        default:
          Sentry.captureException(new Error(`Undefined code style: ${style}`));
          return <></>;
      }
    }
    case 'bullet-list': {
      return (
        <StepBase.Container>
          {block.entries.map((entry, index) => (
            <StepBase.BulletedLine key={index}>
              {entry.text}
            </StepBase.BulletedLine>
          ))}
        </StepBase.Container>
      );
    }
    case 'learn-more': {
      const { title, links } = block;
      return (
        <LearnMoreAccordion
          title={title ? title : 'Learn More'}
          className="w-full"
        >
          {links.map(({ text, url, url_title }, i) => {
            return (
              <StepBase.TextWithLink
                key={i}
                label={text}
                url={url}
                urlTitle={url_title}
              />
            );
          })}
        </LearnMoreAccordion>
      );
    }
    case 'table': {
      return (
        <div className="flex justify-start items-center overflow-x-auto">
          <Table
            className="max-w-max"
            header={
              <Table.HeaderRow>
                {block.column_definitions?.map((col) => (
                  <Table.HeaderCell
                    key={col.title}
                    className="whitespace-nowrap"
                  >
                    <div className="flex gap-2">
                      {col.title}
                      {col.info_text && (
                        <AtomicTooltip
                          tooltipContent={
                            <UpgradePlanStepMarkdown
                              content={col.info_text}
                              defaultTextClass="color-inherit"
                              defaultLinkClass="text-label-bold"
                            />
                          }
                        >
                          <SubframeCore.Icon name="FeatherInfo" />
                        </AtomicTooltip>
                      )}
                    </div>
                  </Table.HeaderCell>
                ))}
              </Table.HeaderRow>
            }
          >
            {block?.data?.map((row, i) => {
              return (
                <Table.Row key={i} clickable={true}>
                  {row?.map((cell, j) => (
                    <Table.Cell key={j} className="p-3 h-auto">
                      {renderTableCell(i, j, cell, context)}
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            })}
          </Table>
        </div>
      );
    }
    case 'checkbox-group': {
      return (
        <StepBase.Container>
          <SubframeCore.Text variant="body">{block.label}</SubframeCore.Text>
          <Stack>
            {block.options.map((item, i) => {
              return <Checkbox key={i} label={item.text} />;
            })}
          </Stack>
        </StepBase.Container>
      );
    }
    case 'option-group': {
      return (
        <StepBase.Container>
          <SubframeCore.Text variant="body">{block.label}</SubframeCore.Text>
          <RadioGroup defaultValue={block.options[0].text}>
            {block.options.map((item, i) => {
              return (
                <RadioGroup.Option
                  key={i}
                  label={item.text}
                  value={item.text}
                />
              );
            })}
          </RadioGroup>
        </StepBase.Container>
      );
    }
    case 'completionCtaCard': {
      if (context.mode !== UpgradeViewMode.Assessment) {
        return <></>;
      }

      return (
        <CtaCard
          onClick={() => {
            context.navigateToRequestTemplate();
          }}
          className="h-auto w-full flex-none"
          leftSlot={
            <IconWithBackground
              variant="success"
              size="medium"
              icon="FeatherCheck"
            />
          }
        >
          <span className="text-subheader font-subheader text-default-font">
            {block.title}
          </span>
          <span className="text-body font-body text-default-font">
            {block.text}
          </span>
        </CtaCard>
      );
    }
    case 'banner': {
      return (
        <Alert
          className={styles.fullWidth}
          variant="warning"
          title={block.title}
          description={<UpgradePlanStepMarkdown content={block.description} />}
        />
      );
    }
    case 'placeholder-banner': {
      // same as banner but with some additional metadata for automation use
      return (
        <Alert
          className={styles.fullWidth}
          variant="warning"
          title={block.title}
          description={<UpgradePlanStepMarkdown content={block.description} />}
        />
      );
    }
    case 'spacer': {
      return <></>;
    }
    case 'info-section': {
      return (
        <InformationalHeader>
          {block.items.map((item, index) => {
            const { title, value, icon } = item;
            return (
              <InformationalHeader.Item
                key={index}
                title={title}
                iconName={
                  icon !== undefined ? (icon as SubframeCore.IconName) : null
                }
                bodySlot={
                  <span className="text-body-bold font-body-bold text-default-font">
                    {value}
                  </span>
                }
              />
            );
          })}
        </InformationalHeader>
      );
    }
    case 'text-input': {
      return (
        <StepBase.Container>
          <SubframeCore.Text variant="body">{block.label}</SubframeCore.Text>
          {block.style === 'multi-line' && (
            <TextArea label="" className="w-full" error={false}>
              <TextArea.Input
                className="self-stretch"
                disabled={getStageDisabled(context.stage)}
              />
            </TextArea>
          )}
          {block.style === 'single-line' && (
            <TextField>
              <TextField.Input
                className="self-stretch"
                disabled={getStageDisabled(context.stage)}
              />
            </TextField>
          )}
        </StepBase.Container>
      );
    }
    case 'canaries': {
      return (
        <Stack direction="row" spacing={2} className="flex-wrap">
          {block.canaries.map((canary, i) => {
            return (
              <CardBase
                key={i}
                title={canary.title}
                cardLabel={<Badge variant="neutral">{canary.badge}</Badge>}
                subtitle=""
                actions={null}
                metric1Children={
                  <CardBase.Metric
                    color={canary['before-color']}
                    label="Before Upgrade"
                    amount={canary.before}
                  />
                }
                metric2Children={
                  <CardBase.Metric
                    color={canary['after-color']}
                    label="After Upgrade"
                    amount={canary.after}
                  />
                }
              />
            );
          })}
        </Stack>
      );
    }
    case 'check': {
      let checkVariant: React.ComponentProps<typeof CheckBase>['variant'];
      switch (block.status) {
        case 'default':
          checkVariant = 'default';
          break;
        case 'success':
          checkVariant = 'success';
          break;
        case 'error':
          checkVariant = 'error';
          break;
        case 'loading':
          checkVariant = 'loading';
          break;
        default:
          Sentry.captureException(
            new Error(`Unexpected status ${block.status}`),
          );
          return <></>;
      }
      return (
        <CheckBase
          variant={checkVariant}
          title={block.title}
          description={block.description}
          checkNumber={block.number?.toString()}
        />
      );
    }
    case 'conversation': {
      if (context.step === undefined) {
        // this only works in step context right now
        return <></>;
      }
      Sentry.captureException('Conversation block encountered', {
        level: 'log',
      });
      return <></>;
    }
    case 'divider-line': {
      // return divider line component
      return <Divider variant="neutral" />;
    }
    case 'accordion-underline': {
      return (
        <>
          <span className="text-subheader font-subheader text-default-font">
            {block.title}
          </span>
          <AccordionWithUnderline headerText={block.description}>
            {block?.entries &&
              block.entries.map((subBlock, idx) => (
                <RenderBlock
                  key={idx}
                  block={subBlock}
                  context={context}
                  onChange={onChange}
                />
              ))}
          </AccordionWithUnderline>
        </>
      );
    }
    case 'sharcflow': {
      const sharcFlowId = block?.sharcflow_id;
      return (
        <>
          {sharcFlowId ? (
            <SHARCFlowCheckResults
              SHARCFlowId={sharcFlowId}
              ExampleData={context.exampleData}
            />
          ) : (
            <></>
          )}
        </>
      );
    }

    default:
      Sentry.captureException(
        new Error(`Unexpected block type encountered: ${block.type}`),
      );
      return <></>;
  }
}
